import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Spinner from '../../../../components/Spinner';
import ManagerStatementService from '../../../../services/managerStatement.service';
import CommonService from '../../../../services/common.service';
import useFetch from '../../../../hooks/useFetch';
import { ManagerStatement } from '../../../../interfaces/managerStatement.interface';
import { AuthContext } from '../../../../contexts/AuthContext';
import { RoleCodes } from '../../../../enums';
import { GENERIC_LOOKUP_KEYS } from '../../../../utils/appConstant';
import Header from './components/Header';
import PrimaryDetails from './components/PrimaryDetails';
import AuditCards from './components/AuditCards';
import Tabs from './components/Tabs';
import DetailsTab, { DetailsTabHandle } from './components/DetailsTab';
import DocumentsTab from './components/DocumentsTab';
import PaymentsTab from './components/PaymentsTab';

const monthName = (month: number) => new Date(2000, month - 1, 1).toLocaleString('en-US', { month: 'long' });
const LOCKED_STATUSES = ['completed', 'paid'];

const ManagerStatementDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { isSystemAdmin, loggedInUser } = useContext(AuthContext);
  const isAdminCaller = isSystemAdmin === 'true' || loggedInUser?.role_code === RoleCodes.admin;

  const [statement, setStatement] = useState<ManagerStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [tab, setTab] = useState<'details' | 'documents' | 'payments'>('details');
  const detailsTabRef = useRef<DetailsTabHandle>(null);

  const { data: statusOptions } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.managerStatementStatus, searchText: '' },
    dependency: [],
  });

  const loadStatement = useCallback((silent = false) => {
    if (!id) return;

    if (!silent) {
      setLoading(true);
      setNotFound(false);
    }

    ManagerStatementService.get(id)
      .then((response: any) => {
        setStatement(response ?? null);
        if (!silent) setLoading(false);
      })
      .catch(() => {
        if (!silent) {
          setNotFound(true);
          setLoading(false);
        }
      });
  }, [id]);

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  // Re-sync with the server whenever this tab regains focus, so a status change made
  // elsewhere (another browser tab, another user) while this tab was in the background
  // is reflected before the viewer acts on stale Upload/Re-upload/Delete visibility.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadStatement(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadStatement]);

  const handleTabChange = useCallback((_: any, value: 'details' | 'documents' | 'payments') => {
    // Any switch that leaves Details (to Documents or Payments) autosaves pending edits (see
    // EVYA-1451). Every other transition (e.g. Documents -> Details, Documents -> Payments)
    // keeps the plain, immediate switch.
    if (tab === 'details' && (value === 'documents' || value === 'payments')) {
      (detailsTabRef.current?.trySwitchAwayFromDetails(value) ?? Promise.resolve(true)).then((shouldSwitch) => {
        if (shouldSwitch) {
          setTab(value);
        }
      });
      return;
    }

    setTab(value);
  }, [tab]);

  const handleDetailsSaved = useCallback((switchTarget: 'none' | 'documents' | 'payments') => {
    loadStatement(true);
    if (switchTarget !== 'none') {
      setTab(switchTarget);
    }
  }, [loadStatement]);

  const handleLinked = useCallback(() => {
    loadStatement(true);
  }, [loadStatement]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-6">
        <Spinner variant="primary" />
      </div>
    );
  }

  if (notFound || !statement) {
    return (
      <div className="p-6 text-center" data-testid="manager-statement-not-found">
        {t('reconciliation.managerStatement.detail.pageTexts.notFound')}
      </div>
    );
  }

  const period = `${monthName(statement.month)} ${statement.year}`;
  // EVYA-1421: Completed/Paid lock applies to everyone; Ready for Review additionally
  // locks Commission Payouts/Transactions for a Manager (already submitted, awaiting
  // admin action) while an Admin caller can still edit at that status.
  const isLocked = LOCKED_STATUSES.includes(statement.status)
    || (!isAdminCaller && statement.status === 'ready_for_review');
  // EVYA-1450: same formula as isLocked, kept as its own value since it governs the
  // Manager Invoice document specifically and is intentionally independent of the
  // Commission Payout/Transaction lock above (see EVYA-1450 TechBrainstorm, Option 2).
  const isInvoiceLocked = LOCKED_STATUSES.includes(statement.status)
    || (!isAdminCaller && statement.status === 'ready_for_review');
  const tabsDisabled = !statement.details_saved_at;

  return (
    <>
      <Header />

      <PrimaryDetails
        loading={loading}
        statement={statement}
        period={period}
        statusOptions={statusOptions}
      />

      <AuditCards statement={statement} />

      <div className="card mb-6">
        <Tabs tab={tab} tabsDisabled={tabsDisabled} onTabChange={handleTabChange} />

        <hr className="m-0" />

        {tab === 'details' && (
          <DetailsTab
            ref={detailsTabRef}
            statementId={String(statement.id)}
            managerName={statement.manager_name}
            notes={statement.notes}
            cleaningDetails={statement.cleaning_details}
            cleaningSplitAmount={statement.cleaning_split_amount}
            period={period}
            status={statement.status}
            statusOptions={statusOptions}
            isLocked={isLocked}
            isAdminCaller={isAdminCaller}
            onDetailsSaved={handleDetailsSaved}
            onLinked={handleLinked}
          />
        )}
        {tab === 'documents' && (
          <DocumentsTab
            statementId={String(statement.id)}
            isInvoiceLocked={isInvoiceLocked}
          />
        )}
        {tab === 'payments' && <PaymentsTab />}
      </div>
    </>
  );
};

export default ManagerStatementDetail;
